// storeSearchController.js - Admin CRM Store Search (#1800): App Store search autocomplete for a term in
// the storefronts picked, through appStoreSearchHints. Apple's endpoint is unofficial and sends no CORS
// headers, so the function asks for the CRM.
import { CARD, EMPTY_STATE, PILL } from "./ui/classes.js";
import { withBusy } from "./ui/busy.js";

// The storefronts appStoreSearchHints knows (STOREFRONTS in functions/app_store_search_hints.js), in the
// order the picker shows them.
const STORES = [
  "US", "GB", "CA", "AU", "NZ", "IE", "DE", "AT", "CH", "FR", "BE", "NL", "IT", "ES", "PT", "SE", "NO",
  "DK", "FI", "PL", "TR", "IL", "AE", "SA", "ZA", "IN", "SG", "HK", "TW", "JP", "KR", "CN", "BR", "MX"
];
const PRESETS = {
  english: ["US", "GB", "CA", "AU", "NZ", "IE"],
  dach: ["DE", "AT", "CH"],
  europe: ["GB", "DE", "FR", "IT", "ES", "NL", "SE", "PL"],
  clear: []
};
const MAX_STORES = 12;
const STORAGE_KEY = "crm.storeSearch.stores";

const HINT_BUTTON =
  "w-full truncate rounded-md px-2 py-1 text-left text-sm text-gray-700 hover:bg-gray-50 hover:text-paprika-700 " +
  "dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white";

const regionNames = typeof Intl !== "undefined" && Intl.DisplayNames
  ? new Intl.DisplayNames(["en"], { type: "region" })
  : null;

let activeDeps = null;

function countryName(code) {
  try {
    return regionNames?.of(code) || code;
  } catch {
    return code;
  }
}

function flag(code) {
  return String.fromCodePoint(...[...code].map((c) => 0x1f1a5 + c.charCodeAt(0)));
}

function escapeHTML(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function readSavedStores() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (Array.isArray(saved)) return saved.filter((c) => STORES.includes(c)).slice(0, MAX_STORES);
  } catch {
    // No storage (private window): the default below.
  }
  return ["US", "GB", "DE"];
}

function saveStores(stores) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stores));
  } catch {
    // Remembering the pick is a convenience only.
  }
}

function pickedStores() {
  return [...document.querySelectorAll("#storeSearchStores input:checked")].map((input) => input.value);
}

function updatePickedCount() {
  const picked = pickedStores();
  const countEl = document.getElementById("storeSearchPickedCount");
  if (countEl) countEl.textContent = `${picked.length} of ${MAX_STORES}`;
  // At the limit, the ones not picked cannot be added.
  document.querySelectorAll("#storeSearchStores input").forEach((input) => {
    input.disabled = !input.checked && picked.length >= MAX_STORES;
    input.closest("label")?.classList.toggle("opacity-40", input.disabled);
  });
  saveStores(picked);
}

function renderStorePicker(selected) {
  const container = document.getElementById("storeSearchStores");
  if (!container) return;
  container.innerHTML = STORES.map((code) => `
    <label class="${PILL}" title="${escapeHTML(countryName(code))}">
      <input type="checkbox" class="sr-only" value="${code}" ${selected.includes(code) ? "checked" : ""}>
      <span aria-hidden="true" class="mr-1">${flag(code)}</span>${code}
    </label>`).join("");
  updatePickedCount();
}

function setPicked(stores) {
  document.querySelectorAll("#storeSearchStores input").forEach((input) => {
    input.checked = stores.includes(input.value);
  });
  updatePickedCount();
}

function renderEmpty(message) {
  const results = document.getElementById("storeSearchResults");
  if (!results) return;
  results.innerHTML = `
    <div class="${EMPTY_STATE.box} sm:col-span-2 xl:col-span-3">
      <p class="${EMPTY_STATE.title}">${escapeHTML(message)}</p>
    </div>`;
}

function renderResults(results) {
  const container = document.getElementById("storeSearchResults");
  if (!container) return;
  container.innerHTML = results.map(({ country, storefront, hints, error }) => {
    let body;
    if (error) {
      body = `<p class="text-sm text-red-600 dark:text-red-400">Apple did not answer: ${escapeHTML(error)}</p>`;
    } else if (!hints.length) {
      body = `<p class="text-sm text-gray-500 dark:text-gray-400">No suggestions.</p>`;
    } else {
      body = `<ol class="space-y-0.5">${hints.map((hint, i) => `
        <li class="flex items-center gap-x-2">
          <span class="w-5 flex-none text-right text-xs text-gray-400 tabular-nums dark:text-gray-500">${i + 1}</span>
          <button type="button" class="${HINT_BUTTON}" data-hint="${escapeHTML(hint)}">${escapeHTML(hint)}</button>
        </li>`).join("")}</ol>`;
    }
    return `
      <div class="${CARD.panel}">
        <div class="flex items-baseline justify-between gap-2 border-b border-gray-200 px-4 py-3 dark:border-white/10">
          <h4 class="${CARD.title}"><span aria-hidden="true" class="mr-1.5">${flag(country)}</span>${escapeHTML(countryName(country))}</h4>
          <span class="font-mono text-xs text-gray-500 dark:text-gray-400">${escapeHTML(String(storefront))}</span>
        </div>
        <div class="px-2 py-3">${body}</div>
      </div>`;
  }).join("");
}

async function runSearch() {
  if (!activeDeps) return;
  const { functions, httpsCallable, showToast } = activeDeps;
  const termInput = document.getElementById("storeSearchTerm");
  const term = (termInput?.value || "").trim();
  const stores = pickedStores();
  if (!term) {
    termInput?.focus();
    return;
  }
  if (!stores.length) {
    showToast?.("Pick at least one storefront.", "error");
    return;
  }

  await withBusy(document.getElementById("storeSearchRunBtn"), async () => {
    try {
      const call = httpsCallable(functions, "appStoreSearchHints");
      const { data } = await call({ term, stores });
      renderResults(data.results || []);
    } catch (err) {
      console.error("❌ App Store suggestions failed:", err);
      showToast?.(`Could not get suggestions: ${err.message}`, "error");
    }
  });
}

export async function loadStoreSearchTemplate() {
  const containerEl = document.getElementById("sectionStoreSearchContainer");
  if (!containerEl) return false;

  try {
    const res = await fetch("templates/storeSearch.html");
    if (!res.ok) throw new Error(`HTTP ${res.status} fetching storeSearch.html`);
    containerEl.innerHTML = await res.text();
    return true;
  } catch (err) {
    console.error("❌ Failed to load store search template:", err);
    return false;
  }
}

// Binds the page's controls and reads nothing: the template is mounted before anyone has signed in
// (#1333), and the page asks Apple only when the admin runs a search.
export function initStoreSearchController(deps) {
  activeDeps = deps;

  renderStorePicker(readSavedStores());
  renderEmpty("Type a term and pick storefronts to see what the App Store suggests.");

  document.getElementById("storeSearchStores")?.addEventListener("change", updatePickedCount);

  document.querySelectorAll(".store-search-preset").forEach((btn) => {
    btn.addEventListener("click", () => setPicked(PRESETS[btn.dataset.preset] || []));
  });

  document.getElementById("storeSearchForm")?.addEventListener("submit", (e) => {
    e.preventDefault();
    runSearch();
  });

  // A suggestion becomes the next search, to follow a phrase down.
  document.getElementById("storeSearchResults")?.addEventListener("click", (e) => {
    const button = e.target.closest?.("[data-hint]");
    if (!button) return;
    const termInput = document.getElementById("storeSearchTerm");
    if (termInput) termInput.value = button.dataset.hint;
    runSearch();
  });
}
